/**
 * Penanda "kapan daftar pemberitahuan terakhir dibuka" — satu-satunya berkas yang menyentuh
 * `localStorage` untuk keperluan ini.
 *
 * SETIAP akses dibungkus `try/catch`, dan itu bukan kehati-hatian berlebihan: pada Safari mode
 * pribadi dan pada peramban yang memblokir data situs, MEMBACA `localStorage` pun melempar —
 * bukan mengembalikan null. Tanpa penjagaan ini, lonceng yang gagal membaca penanda akan
 * menjatuhkan seluruh bilah atas, dan kurir kehilangan navigasinya karena sebuah lencana.
 *
 * Kegagalan apa pun diperlakukan sebagai "belum pernah membuka": pemberitahuan tetap tampil,
 * hanya tidak pernah berhenti tampil. Arah gagal itu disengaja — lencana yang muncul terlalu
 * sering mengganggu, lencana yang diam-diam hilang menyembunyikan pekerjaan.
 */

import { ambilPenandaDariSearch, penandaBaru } from './penanda-dibaca';

const KUNCI = 'workforce:pemberitahuan:terakhir-dilihat';

export function bacaTerakhirDilihat(): string | null {
  try {
    const nilai = localStorage.getItem(KUNCI);
    if (!nilai) return null;
    // Nilai rusak (disunting tangan, atau sisa versi lama) diperlakukan seperti tidak ada.
    return Number.isNaN(Date.parse(nilai)) ? null : nilai;
  } catch {
    return null;
  }
}

export function tulisTerakhirDilihat(waktuIso: string): void {
  try {
    localStorage.setItem(KUNCI, waktuIso);
  } catch {
    /* Penanda gagal disimpan → daftarnya tetap tampil lain kali. Tidak ada yang perlu digagalkan. */
  }
}

/**
 * Majukan penanda ke titik yang dibawa ketukan notifikasi HP (#1756) — tidak pernah memundurkan.
 * `true` bila penanda benar-benar berubah. Aturan maju/batas ada di `penandaBaru`.
 */
export function majukanTerakhirDilihat(kandidatIso: string, sekarang: Date = new Date()): boolean {
  const baru = penandaBaru(bacaTerakhirDilihat(), kandidatIso, sekarang);
  if (baru === null) return false;
  tulisTerakhirDilihat(baru);
  return bacaTerakhirDilihat() === baru;
}

/**
 * Terapkan penanda `?dibaca=…` dari alamat halaman (#1756) — dipanggil `main.tsx` SEBELUM render.
 *
 * Dipisah dari `main.tsx` supaya dapat diuji (temuan review independen #1757): sebelumnya baris
 * yang memajukan penanda dapat dihapus tanpa satu uji pun merah, dan bug laporan Owner kembali
 * persis seperti semula. `gantiAlamat` menerima alamat TANPA parameter penanda (parameter lain dan
 * hash dipertahankan) — di aplikasi ia `history.replaceState`, supaya memuat ulang atau membagikan
 * tautan tidak menerapkan penanda yang sama lagi. Tanpa penanda di alamat: tidak melakukan apa pun.
 */
export function terapkanPenandaDariLokasi(
  lokasi: { readonly pathname: string; readonly search: string; readonly hash: string },
  gantiAlamat: (alamat: string) => void,
  sekarang: Date = new Date(),
): void {
  const { dibacaSampai, sisa } = ambilPenandaDariSearch(lokasi.search);
  if (dibacaSampai === null) return;
  majukanTerakhirDilihat(dibacaSampai, sekarang);
  gantiAlamat(`${lokasi.pathname}${sisa}${lokasi.hash}`);
}
